import Axios from 'axios';
import type { AxiosInstance } from 'axios';
import {
  AxiosCacheInstance,
  buildStorage,
  setupCache,
  StorageValue,
} from 'axios-cache-interceptor';
import localforage from 'localforage';
import { isString } from 'lodash';
import md5 from 'md5';

import serializeQuery from '../helpers/serializeQuery';

// Create `localforage` instance
const forageStore = localforage.createInstance({
  // List of drivers used
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  // Prefix all storage keys to prevent conflicts
  name: 'aucache',
});

export const cachedAxios = setupCache(Axios.create(), {
  // Need to disable cache takeover to prevent "Cache-Control", "Pragma" and "expires"
  // headers from being added to the request automatically by axios-cache-interceptor.
  // We turn takeover off because adding cache-control headers occasionally result into
  // a CORS error.
  // @see https://github.com/arthurfiorette/axios-cache-interceptor/issues/471
  cacheTakeover: false,
  // Store URLs as storage key strings
  generateKey: req => {
    if (req.id) {
      return req.id;
    }
    const url = new URL(req.url || '', req?.baseURL);
    const key = url.href + serializeQuery(req);
    return req.data ? key + md5(req.data as string) : key;
  },
  storage: buildStorage({
    async set(key, value) {
      await forageStore.setItem(key, value);
    },
    async find(key) {
      try {
        const item = await forageStore.getItem<StorageValue>(key);
        if (item === null) {
          return undefined;
        }
        return item;
      } catch (error) {
        return undefined;
      }
    },
    async remove(key) {
      return forageStore.removeItem(key);
    },
  }),
});

/**
 * Get axios instance depending on caching preference
 *
 * @param {boolean} bypassCache a flag whether to use cached axios instance or the vanilla uncached axios.
 */
export function getAxiosInstance(bypassCache: boolean): AxiosInstance;
export function getAxiosInstance(bypassCache?: boolean): AxiosCacheInstance;
export function getAxiosInstance(bypassCache: unknown): unknown {
  return bypassCache ? Axios : cachedAxios;
}

/**
 * Build cache key
 * @param {string[]} dependencies dependencies
 * @returns {string} key to be further used as cache ID for axios-cache-interceptor
 */
export function buildCacheKey(dependencies: Array<string | undefined>) {
  return dependencies.filter(v => isString(v) && v).join(':');
}
